/* eslint-disable */
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Box } from "@mui/material";

interface ZoomableImageProps {
  imgSrc: string;
  onColorPick: (color: string) => void;
  zoomLevel?: number;
}

export const ZoomableImage = ({
  imgSrc,
  onColorPick,
  zoomLevel = 3,
}: ZoomableImageProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [showZoom, setShowZoom] = useState(false);
  const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 });
  const [imgLoaded, setImgLoaded] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const zoomCanvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const [imageDimensions, setImageDimensions] = useState({
    width: 0,
    height: 0,
  });

  // Calculate and set the proper canvas size based on container and image dimensions
  const calculateCanvasSize = useCallback(() => {
    if (
      !containerRef.current ||
      !imageDimensions.width ||
      !imageDimensions.height
    )
      return;

    const containerWidth = containerRef.current.clientWidth;
    const containerHeight = containerRef.current.clientHeight;

    // Calculate the aspect ratio
    const imageRatio = imageDimensions.width / imageDimensions.height;

    let width,
      height,
      offsetX = 0,
      offsetY = 0;

    // If image is wider than container (using container width as constraint)
    if (imageDimensions.width > containerWidth) {
      width = containerWidth;
      height = width / imageRatio;
    } else {
      // Use original image dimensions
      width = imageDimensions.width;
      height = imageDimensions.height;
    }

    // Ensure height doesn't exceed container (max height constraint)
    if (height > containerHeight) {
      height = containerHeight;
      width = height * imageRatio;
    }

    // Calculate offsets for centering
    offsetX = (containerWidth - width) / 2;
    offsetY = (containerHeight - height) / 2;

    // Apply dimensions and positioning directly to canvas
    if (canvasRef.current) {
      canvasRef.current.style.width = `${width}px`;
      canvasRef.current.style.height = `${height}px`;
      canvasRef.current.style.marginLeft = `${offsetX}px`;
      canvasRef.current.style.marginTop = `${offsetY}px`;
    }
  }, [imageDimensions]);

  // Load the image and set its dimensions
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      // Store original image dimensions
      setImageDimensions({ width: img.width, height: img.height });
      setImgLoaded(true);

      // Draw image on canvas (full size)
      if (canvasRef.current) {
        const ctx = canvasRef.current.getContext("2d");
        if (ctx) {
          canvasRef.current.width = img.width;
          canvasRef.current.height = img.height;
          ctx.drawImage(img, 0, 0, img.width, img.height);
        }
      }

      // Hide the original image
      if (imageRef.current) {
        imageRef.current.style.display = "none";
      }
    };
    img.src = imgSrc;
    if (imageRef.current) {
      imageRef.current.src = imgSrc;
    }
  }, [imgSrc]);

  // Calculate the canvas size when image dimensions change or container resizes
  useEffect(() => {
    calculateCanvasSize();

    // Also recalculate on window resize
    const handleResize = () => {
      calculateCanvasSize();
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [imageDimensions, calculateCanvasSize]);

  const drawZoom = useCallback(
    (x: number, y: number) => {
      if (!canvasRef.current || !zoomCanvasRef.current) return;

      const mainCtx = canvasRef.current.getContext("2d");
      const zoomCtx = zoomCanvasRef.current.getContext("2d");

      if (!mainCtx || !zoomCtx) return;

      const zoomSize = 100; // Size of the zoom canvas
      const sourceSize = zoomSize / zoomLevel;
      const halfSourceSize = sourceSize / 2;

      // Clear zoom canvas with white background
      zoomCtx.fillStyle = "white";
      zoomCtx.fillRect(0, 0, zoomSize, zoomSize);

      // Calculate source coordinates, allowing values outside the image bounds
      const sourceX = x - halfSourceSize;
      const sourceY = y - halfSourceSize;

      // Only draw the portion of the source that's within the image bounds
      // First, determine if any part of the source area is within the image
      const sourceRightEdge = sourceX + sourceSize;
      const sourceBottomEdge = sourceY + sourceSize;

      const hasIntersection =
        sourceRightEdge > 0 &&
        sourceX < canvasRef.current.width &&
        sourceBottomEdge > 0 &&
        sourceY < canvasRef.current.height;

      if (hasIntersection) {
        // Calculate the visible portion of the source rectangle
        const visibleSourceX = Math.max(0, sourceX);
        const visibleSourceY = Math.max(0, sourceY);
        const visibleSourceWidth = Math.min(
          canvasRef.current.width - visibleSourceX,
          sourceSize - (visibleSourceX - sourceX)
        );
        const visibleSourceHeight = Math.min(
          canvasRef.current.height - visibleSourceY,
          sourceSize - (visibleSourceY - sourceY)
        );

        // Calculate the position on the zoom canvas
        const zoomX = (visibleSourceX - sourceX) * (zoomSize / sourceSize);
        const zoomY = (visibleSourceY - sourceY) * (zoomSize / sourceSize);
        const zoomWidth = visibleSourceWidth * (zoomSize / sourceSize);
        const zoomHeight = visibleSourceHeight * (zoomSize / sourceSize);

        // Draw the visible portion of the image onto the zoom canvas
        zoomCtx.drawImage(
          canvasRef.current,
          visibleSourceX,
          visibleSourceY,
          visibleSourceWidth,
          visibleSourceHeight,
          zoomX,
          zoomY,
          zoomWidth,
          zoomHeight
        );
      }

      // Draw grid
      zoomCtx.strokeStyle = "rgba(79, 79, 79, 0.5)";
      zoomCtx.lineWidth = 2;

      // Draw vertical grid lines
      const gridSize = Math.max(10, zoomLevel * 2); // Increased grid size for wider squares
      for (let i = 0; i <= zoomSize; i += gridSize) {
        zoomCtx.beginPath();
        zoomCtx.moveTo(i, 0);
        zoomCtx.lineTo(i, zoomSize);
        zoomCtx.stroke();
      }

      // Draw horizontal grid lines
      for (let i = 0; i <= zoomSize; i += gridSize) {
        zoomCtx.beginPath();
        zoomCtx.moveTo(0, i);
        zoomCtx.lineTo(zoomSize, i);
        zoomCtx.stroke();
      }

      // Draw border around pixel at center with red highlight
      zoomCtx.strokeStyle = "rgba(255, 0, 0, 0.9)"; // Red highlight
      zoomCtx.lineWidth = 2;

      // Make the center highlight match the grid size
      zoomCtx.strokeRect(
        zoomSize / 2 - gridSize / 2,
        zoomSize / 2 - gridSize / 2,
        gridSize,
        gridSize
      );
    },
    [zoomLevel]
  );

  const getPixelColor = useCallback((x: number, y: number): string => {
    if (!canvasRef.current) return "#000000";

    const ctx = canvasRef.current.getContext("2d");
    if (!ctx) return "#000000";

    // Check if coordinates are within bounds
    if (
      x < 0 ||
      y < 0 ||
      x >= canvasRef.current.width ||
      y >= canvasRef.current.height
    ) {
      return "transparent"; // Return transparent for out-of-bounds
    }

    // Get pixel data
    const pixelData = ctx.getImageData(x, y, 1, 1).data;

    // Convert to hex
    return `#${(
      (1 << 24) |
      (pixelData[0] << 16) |
      (pixelData[1] << 8) |
      pixelData[2]
    )
      .toString(16)
      .slice(1)}`;
  }, []);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!containerRef.current || !imgLoaded || !canvasRef.current) return;

      const canvasRect = canvasRef.current.getBoundingClientRect();

      // Check if mouse is inside canvas
      if (
        e.clientX < canvasRect.left ||
        e.clientX > canvasRect.right ||
        e.clientY < canvasRect.top ||
        e.clientY > canvasRect.bottom
      ) {
        setShowZoom(false);
        return;
      }

      // Calculate the scale factor between original image and displayed canvas
      const scaleX = canvasRef.current.width / canvasRect.width;
      const scaleY = canvasRef.current.height / canvasRect.height;

      // Calculate cursor position on the original image
      const x = Math.floor((e.clientX - canvasRect.left) * scaleX);
      const y = Math.floor((e.clientY - canvasRect.top) * scaleY);

      // Position the zoom lens (relative to page/document, not container)
      // This allows it to exceed the image boundaries
      const pageX = e.clientX;
      const pageY = e.clientY;

      setZoomPosition({
        x: pageX + 20, // Offset to not cover cursor
        y: pageY - 120, // Position above cursor
      });

      drawZoom(x, y);
      setShowZoom(true);
    },
    [imgLoaded, drawZoom]
  );

  const handleMouseLeave = useCallback(() => {
    setShowZoom(false);
  }, []);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!containerRef.current || !imgLoaded || !canvasRef.current) return;

      const canvasRect = canvasRef.current.getBoundingClientRect();

      // Check if click is inside canvas
      if (
        e.clientX < canvasRect.left ||
        e.clientX > canvasRect.right ||
        e.clientY < canvasRect.top ||
        e.clientY > canvasRect.bottom
      ) {
        return;
      }

      // Calculate the scale factor between original image and displayed canvas
      const scaleX = canvasRef.current.width / canvasRect.width;
      const scaleY = canvasRef.current.height / canvasRect.height;

      // Calculate cursor position on the original image
      const x = Math.floor((e.clientX - canvasRect.left) * scaleX);
      const y = Math.floor((e.clientY - canvasRect.top) * scaleY);

      const color = getPixelColor(x, y);
      onColorPick(color);
    },
    [imgLoaded, getPixelColor, onColorPick]
  );

  return (
    <Box
      ref={containerRef}
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        minHeight: "200px",
        cursor: "crosshair",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        overflow: "visible", // Change from 'hidden' to 'visible'
      }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
    >
      {/* Reference image (hidden) */}
      <img
        ref={imageRef}
        src={imgSrc}
        alt="Color picker"
        style={{
          maxWidth: "100%",
          maxHeight: "100%",
          display: "block",
          opacity: 0,
          position: "absolute",
        }}
      />

      {/* Main canvas for drawing and picking */}
      <canvas
        ref={canvasRef}
        style={{
          maxWidth: "100%",
          maxHeight: "100%",
          display: "block",
          position: "relative",
        }}
      />

      {/* Zoom lens - positioned fixed relative to viewport */}
      {showZoom && imgLoaded && (
        <Box
          sx={{
            position: "fixed", // Change from 'absolute' to 'fixed'
            left: `${zoomPosition.x}px`,
            top: `${zoomPosition.y}px`,
            width: "100px",
            height: "100px",
            border: "2px solid white",
            borderRadius: "50%",
            boxShadow: "0 0 5px rgba(0,0,0,0.5)",
            overflow: "hidden",
            zIndex: 9999, // Very high z-index to appear above everything
            backgroundColor: "white",
          }}
        >
          <canvas ref={zoomCanvasRef} width={100} height={100} />
        </Box>
      )}
    </Box>
  );
};

export default ZoomableImage;
